'use client';

import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import omit from 'lodash.omit';
import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useReducer,
  useRef
} from 'react';
import NextSelection from '~/constants/new-cars/NextSelection';
import SectionName from '~/constants/new-cars/SectionName';
import type {
  ConfigurationResponse,
  CustomizeRequest,
  CustomizeResponse,
  ModelVersionConfigurationResponse,
  NewCarsVehicle,
  ParentModelConfigurationResponse,
  SelectedDoors,
  SelectedModelVersion,
  SelectedParentModel,
  SelectedTrim,
  SelectedTruckBodyStyle,
  SelectedYear,
  TruckBodyStyleConfigurationResponse,
  YearConfigurationResponse
} from '~/contracts/new-cars';
import type { DeepPartial } from '~/types';
import { readNewCarsSectionSequence } from '~/utilities/new-cars/newCarsHistoryState';
import configuratorReducer, {
  type ConfiguratorAction
} from './configuratorReducer';
import { getInitialState, type UrlParams } from './configuratorState';
import useConfiguratorUrlSync from './useConfiguratorUrlSync';
import deriveActiveSection from './utilities/deriveActiveSection';
import deriveSectionSequence from './utilities/deriveSectionSequence';

function generateDefaultResponse<C extends ConfigurationResponse>(
  context: Partial<C>
): C {
  return {
    count: 0,
    imageUrl: '',
    matchingVehicles: new Array<NewCarsVehicle>(),
    results: [] as C['results'],
    totalMatchingVehicles: 0,
    ...context
  } as C;
}

export type ParentModelContextType = {
  activeSection: SectionName;
  clearSelection: (section: SectionName) => void;
  customizeOptions: LoadableState<CustomizeResponse>;
  isFirstSection: (name: SectionName) => boolean;
  isSectionIncluded: (name: SectionName) => boolean;
  modelVersions: LoadableState<YearConfigurationResponse>;
  resetSelectedOptions: () => void;
  sectionSequence: SectionName[];
  selectedDoors: SelectedDoors | null;
  selectedModelVersion: SelectedModelVersion | null;
  selectedOptions: CustomizeRequest | null;
  selectedParentModel: SelectedParentModel;
  selectedTrim: SelectedTrim | null;
  selectedTruckBodyStyle: SelectedTruckBodyStyle | null;
  selectedYear: SelectedYear | null;
  setSelectedDoors: (
    value: SelectedDoors | null,
    options?: { silent?: boolean }
  ) => void;
  setSelectedModelVersion: (
    value: SelectedModelVersion | null,
    options?: { silent?: boolean }
  ) => void;
  setSelectedOptions: (value: CustomizeRequest | null) => void;
  setSelectedTrim: (value: SelectedTrim | null) => void;
  setSelectedTruckBodyStyle: (value: SelectedTruckBodyStyle | null) => void;
  setSelectedYear: (value: SelectedYear | null) => void;
  startOver: () => void;
  suppressNextDisplayFetchRef: React.RefObject<boolean>;
  trims: LoadableState<ModelVersionConfigurationResponse>;
  truckBodyStyles: LoadableState<TruckBodyStyleConfigurationResponse>;
  years: ParentModelConfigurationResponse;
};

export const defaultParentModelContext: ParentModelContextType = {
  activeSection: SectionName.ModelVersion,
  clearSelection() {},
  customizeOptions: defaultLoadableState<CustomizeResponse>({
    basePrice: 0,
    closestVehicle: null,
    driveTypes: [],
    engines: [],
    exteriorColors: [],
    imageUrl: '',
    interiorColors: [],
    make: '',
    matchingVehicles: [],
    model: '',
    modelVersion: '',
    packageOptions: [],
    parentModel: '',
    totalMatchingVehicles: 0,
    trim: '',
    year: 0
  }),
  isFirstSection: (name: SectionName) => name === SectionName.ModelVersion,
  isSectionIncluded: () => true,
  modelVersions: defaultLoadableState(
    generateDefaultResponse<YearConfigurationResponse>({
      make: '',
      parentModel: '',
      year: 0
    })
  ),
  resetSelectedOptions() {},
  sectionSequence: [
    SectionName.ModelVersion,
    SectionName.Trim,
    SectionName.Options
  ],
  selectedDoors: null,
  selectedModelVersion: null,
  selectedOptions: null,
  selectedParentModel: {
    count: 0,
    imageUrl: '',
    matchingVehicles: [],
    make: '',
    model: '',
    parentModel: '',
    totalMatchingVehicles: 0,
    zipcode: ''
  },
  selectedTrim: null,
  selectedTruckBodyStyle: null,
  selectedYear: null,
  setSelectedDoors() {},
  setSelectedModelVersion() {},
  setSelectedOptions() {},
  setSelectedTrim() {},
  setSelectedTruckBodyStyle() {},
  setSelectedYear() {},
  startOver() {},
  suppressNextDisplayFetchRef: { current: false },
  trims: defaultLoadableState(
    generateDefaultResponse<ModelVersionConfigurationResponse>({
      make: '',
      modelVersion: '',
      parentModel: '',
      year: 0
    })
  ),
  truckBodyStyles: defaultLoadableState(
    generateDefaultResponse<TruckBodyStyleConfigurationResponse>({
      make: '',
      modelVersion: '',
      parentModel: '',
      trim: '',
      truckBodyStyle: '',
      year: 0
    })
  ),
  years: generateDefaultResponse<YearConfigurationResponse>({
    make: '',
    parentModel: '',
    year: 0
  })
};

const ParentModelContext = createContext<ParentModelContextType>(
  defaultParentModelContext
);

export type OwnProps = PropsWithChildren<{
  initialModelVersionResponse: ModelVersionConfigurationResponse | null;
  initialParentModelResponse: ParentModelConfigurationResponse;
  initialTruckBodyStyleResponse: TruckBodyStyleConfigurationResponse | null;
  initialYearsResponse: YearConfigurationResponse | null;
  urlParams?: UrlParams;
}>;

export function ParentModelContextProvider({
  children,
  initialModelVersionResponse,
  initialParentModelResponse,
  initialTruckBodyStyleResponse,
  initialYearsResponse,
  urlParams
}: OwnProps) {
  const modelVersions = useLoadableState<YearConfigurationResponse>(
    initialYearsResponse ?? defaultParentModelContext.modelVersions.data,
    { initializeAsLoaded: Boolean(initialYearsResponse) }
  );
  const trims = useLoadableState<ModelVersionConfigurationResponse>(
    initialModelVersionResponse ?? defaultParentModelContext.trims.data,
    { initializeAsLoaded: Boolean(initialModelVersionResponse) }
  );
  const truckBodyStyles = useLoadableState<TruckBodyStyleConfigurationResponse>(
    initialTruckBodyStyleResponse ??
      defaultParentModelContext.truckBodyStyles.data,
    { initializeAsLoaded: Boolean(initialTruckBodyStyleResponse) }
  );
  const customizeOptions = useLoadableState<CustomizeResponse>(
    defaultParentModelContext.customizeOptions.data
  );

  // Read saved section sequence once (client-side only). When restoring after a
  // refresh, truckBodyStyles is only server-prefetched when the URL carries a
  // truckBodyStyle deep-link; otherwise its results start empty, which would
  // omit TruckBodyStyle from the initial sequence. The saved sequence lets us
  // patch it back in so the section renders in the DOM and animateInstant can
  // show it before the CSR fetch completes.
  const savedSectionSequence = useMemo<SectionName[] | null>(
    () => readNewCarsSectionSequence(),
    []
  );

  // The API names the next user-facing selection via nextSelection. Show the
  // Year section only when it returns 'Year'; a single-year vehicle skips it and
  // returns a deeper level instead. Fall back to the result count when the field
  // is empty (no explicit next selection).
  const showYearSection =
    initialParentModelResponse.nextSelection === NextSelection.Year ||
    (initialParentModelResponse.nextSelection === '' &&
      initialParentModelResponse.results.length > 1);

  const sectionSequenceRef = useRef<SectionName[]>([]);
  const sectionSequence = useMemo(() => {
    const derived = deriveSectionSequence(
      modelVersions.data.results,
      truckBodyStyles.data.results,
      showYearSection,
      modelVersions.data.nextSelection
    );
    let result = derived;

    if (
      savedSectionSequence?.includes(SectionName.TruckBodyStyle) &&
      !derived.includes(SectionName.TruckBodyStyle)
    ) {
      const trimIndex = derived.indexOf(SectionName.Trim);
      if (trimIndex !== -1) {
        const patched = [...derived];
        patched.splice(trimIndex + 1, 0, SectionName.TruckBodyStyle);
        result = patched;
      }
    }

    // Return the same reference when values are unchanged so animation effects
    // that depend on sectionSequence don't re-fire mid-transition.
    if (
      result.length === sectionSequenceRef.current.length &&
      result.every((s, i) => s === sectionSequenceRef.current[i])
    ) {
      return sectionSequenceRef.current;
    }
    sectionSequenceRef.current = result;

    return result;
  }, [
    modelVersions.data.nextSelection,
    modelVersions.data.results,
    savedSectionSequence,
    showYearSection,
    truckBodyStyles.data.results
  ]);

  const isFirstSection = useCallback(
    (name: SectionName) => sectionSequence[0] === name,
    [sectionSequence]
  );

  const isSectionIncluded = useCallback(
    (name: SectionName) => sectionSequence.includes(name),
    [sectionSequence]
  );

  const selectedParentModel = useMemo<SelectedParentModel>(
    () => omit(initialParentModelResponse, 'results'),
    [initialParentModelResponse]
  );

  const [state, dispatch] = useReducer(
    configuratorReducer,
    {
      sectionSequence,
      initialModelVersionResponse,
      initialParentModelResponse,
      initialTruckBodyStyleResponse,
      initialYearsResponse,
      urlParams
    },
    getInitialState
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  const lastActionRef = useRef<ConfiguratorAction['type'] | null>(null);
  const skipHistorySyncRef = useRef(false);

  const wrappedDispatch = useCallback((action: ConfiguratorAction) => {
    lastActionRef.current = action.type;
    dispatch(action);
  }, []);

  const suppressNextDisplayFetchRef = useRef(false);

  const setSelectedYear = useCallback(
    (year: SelectedYear | null) => {
      wrappedDispatch(
        year ? { type: 'SELECT_YEAR', payload: year } : { type: 'CLEAR_YEAR' }
      );
    },
    [wrappedDispatch]
  );

  const setSelectedDoors = useCallback(
    (doors: SelectedDoors | null, options?: { silent?: boolean }) => {
      if (options?.silent) {
        skipHistorySyncRef.current = true;
      }

      wrappedDispatch(
        doors
          ? { type: 'SELECT_DOORS', payload: doors }
          : { type: 'CLEAR_DOORS' }
      );
    },
    [wrappedDispatch]
  );

  const setSelectedModelVersion = useCallback(
    (mv: SelectedModelVersion | null, options?: { silent?: boolean }) => {
      if (options?.silent) {
        skipHistorySyncRef.current = true;
      }

      wrappedDispatch(
        mv
          ? { type: 'SELECT_MODEL_VERSION', payload: mv }
          : { type: 'CLEAR_MODEL_VERSION' }
      );
    },
    [wrappedDispatch]
  );

  const setSelectedTrim = useCallback(
    (trim: SelectedTrim | null) => {
      wrappedDispatch(
        trim ? { type: 'SELECT_TRIM', payload: trim } : { type: 'CLEAR_TRIM' }
      );
    },
    [wrappedDispatch]
  );

  const setSelectedTruckBodyStyle = useCallback(
    (tbs: SelectedTruckBodyStyle | null) => {
      wrappedDispatch(
        tbs
          ? { type: 'SELECT_TRUCK_BODY_STYLE', payload: tbs }
          : { type: 'CLEAR_TRUCK_BODY_STYLE' }
      );
    },
    [wrappedDispatch]
  );

  const setSelectedOptions = useCallback(
    (options: CustomizeRequest | null) => {
      wrappedDispatch(
        options
          ? { type: 'SET_OPTIONS', payload: options }
          : { type: 'CLEAR_OPTIONS' }
      );
    },
    [wrappedDispatch]
  );

  const clearSelection = useCallback(
    (section: SectionName) => {
      switch (section) {
        case SectionName.Doors:
          return setSelectedDoors(null);
        case SectionName.ModelVersion:
          return setSelectedModelVersion(null);
        case SectionName.Options:
          return setSelectedOptions(null);
        case SectionName.Trim:
          return setSelectedTrim(null);
        case SectionName.TruckBodyStyle:
          return setSelectedTruckBodyStyle(null);
        case SectionName.Year:
          setSelectedYear(null);
          setSelectedModelVersion(null);
          setSelectedDoors(null);
          setSelectedTrim(null);
          setSelectedTruckBodyStyle(null);
          setSelectedOptions(null);

          return;
      }
    },
    [
      setSelectedDoors,
      setSelectedModelVersion,
      setSelectedOptions,
      setSelectedTrim,
      setSelectedTruckBodyStyle,
      setSelectedYear
    ]
  );

  const resetSelectedOptions = useCallback(() => {
    const { selectedTrim, selectedTruckBodyStyle } = stateRef.current;
    const newSelectedOptions = selectedTruckBodyStyle ?? selectedTrim;

    if (newSelectedOptions) {
      wrappedDispatch({
        type: 'SET_OPTIONS',
        payload: { ...newSelectedOptions }
      });
    }
  }, [wrappedDispatch]);

  const startOver = useCallback(() => {
    wrappedDispatch({ type: 'START_OVER' });
  }, [wrappedDispatch]);

  useConfiguratorUrlSync(
    state,
    sectionSequence,
    lastActionRef,
    skipHistorySyncRef,
    wrappedDispatch
  );

  const activeSection = useMemo<SectionName>(() => {
    const optionsCount =
      customizeOptions.data.driveTypes.length +
      customizeOptions.data.engines.length +
      customizeOptions.data.exteriorColors.length +
      customizeOptions.data.interiorColors.length;

    return deriveActiveSection(
      sectionSequence,
      Boolean(state.selectedModelVersion),
      Boolean(state.selectedDoors),
      Boolean(state.selectedYear),
      trims.hasEverLoaded,
      trims.data.results.length,
      Boolean(state.selectedTrim),
      Boolean(state.selectedTruckBodyStyle),
      customizeOptions.hasEverLoaded,
      optionsCount,
      modelVersions.isLoaded
    );
  }, [
    customizeOptions.data.driveTypes,
    customizeOptions.data.engines,
    customizeOptions.data.exteriorColors.length,
    customizeOptions.data.interiorColors.length,
    customizeOptions.hasEverLoaded,
    modelVersions.isLoaded,
    sectionSequence,
    state.selectedDoors,
    state.selectedModelVersion,
    state.selectedTrim,
    state.selectedTruckBodyStyle,
    state.selectedYear,
    trims.data.results.length,
    trims.hasEverLoaded
  ]);

  const value = useMemo<ParentModelContextType>(
    () => ({
      activeSection,
      clearSelection,
      customizeOptions,
      isFirstSection,
      isSectionIncluded,
      modelVersions,
      resetSelectedOptions,
      sectionSequence,
      selectedDoors: state.selectedDoors,
      selectedModelVersion: state.selectedModelVersion,
      selectedOptions: state.selectedOptions,
      selectedParentModel,
      selectedTrim: state.selectedTrim,
      selectedTruckBodyStyle: state.selectedTruckBodyStyle,
      selectedYear: state.selectedYear,
      setSelectedDoors,
      setSelectedModelVersion,
      setSelectedOptions,
      setSelectedTrim,
      setSelectedTruckBodyStyle,
      setSelectedYear,
      startOver,
      suppressNextDisplayFetchRef,
      trims,
      truckBodyStyles,
      years: initialParentModelResponse
    }),
    [
      activeSection,
      clearSelection,
      customizeOptions,
      initialParentModelResponse,
      isFirstSection,
      isSectionIncluded,
      modelVersions,
      resetSelectedOptions,
      sectionSequence,
      selectedParentModel,
      setSelectedDoors,
      setSelectedModelVersion,
      setSelectedOptions,
      setSelectedTrim,
      setSelectedTruckBodyStyle,
      setSelectedYear,
      startOver,
      state.selectedDoors,
      state.selectedModelVersion,
      state.selectedOptions,
      state.selectedTrim,
      state.selectedTruckBodyStyle,
      state.selectedYear,
      trims,
      truckBodyStyles
    ]
  );

  return (
    <ParentModelContext.Provider value={value}>
      {children}
    </ParentModelContext.Provider>
  );
}

ParentModelContextProvider.displayName = 'ParentModelContextProvider';

export function generateParentModelContext(
  customState: DeepPartial<ParentModelContextType>
): ParentModelContextType {
  return deepmerge<ParentModelContextType, DeepPartial<ParentModelContextType>>(
    defaultParentModelContext,
    customState,
    { arrayMerge: (_target, source) => source }
  );
}

export function MockedParentModelContextProvider({
  children,
  mockValue
}: PropsWithChildren<{ mockValue: DeepPartial<ParentModelContextType> }>) {
  return (
    <ParentModelContext.Provider value={generateParentModelContext(mockValue)}>
      {children}
    </ParentModelContext.Provider>
  );
}

export default ParentModelContext;
